import { useState } from 'react'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  TextField,
  Chip,
  IconButton,
  Typography,
  Stack,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import type { FieldAnnotation } from './types.js'

type Props = {
  name: string
  annotation: FieldAnnotation
  onChange: (updated: FieldAnnotation) => void
  onRemove: () => void
}

export const FieldEditor = ({
  name,
  annotation,
  onChange,
  onRemove,
}: Props) => {
  const [exampleInput, setExampleInput] = useState('')

  const addExample = () => {
    const val = exampleInput.trim()
    if (!val) return
    let parsed: unknown
    try {
      parsed = JSON.parse(val)
    } catch {
      parsed = val
    }
    onChange({
      ...annotation,
      examples: [...(annotation.examples ?? []), parsed],
    })
    setExampleInput('')
  }

  const removeExample = (i: number) =>
    onChange({
      ...annotation,
      examples: annotation.examples?.filter((_, idx) => idx !== i),
    })

  return (
    <Accordion disableGutters variant="outlined">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <Typography variant="body2" fontWeight={600} fontFamily="monospace">
            {name}
          </Typography>
          <Box display="flex" alignItems="center" gap={1} mr={1}>
            {annotation.relatesTo && (
              <Chip
                label={`→ ${annotation.relatesTo}`}
                size="small"
                color="success"
                variant="outlined"
              />
            )}
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                onRemove()
              }}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Stack spacing={2}>
          <TextField
            label="Beschreibung"
            size="small"
            fullWidth
            value={annotation.description}
            onChange={(e) =>
              onChange({ ...annotation, description: e.target.value })
            }
            placeholder="Was bedeutet dieses Feld?"
          />

          <TextField
            label="Referenz auf (z.B. machines.id)"
            size="small"
            fullWidth
            value={annotation.relatesTo ?? ''}
            onChange={(e) =>
              onChange({
                ...annotation,
                relatesTo: e.target.value || undefined,
              })
            }
            placeholder="collection.field"
          />

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              display="block"
              mb={1}
            >
              Beispielwerte
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
              {annotation.examples?.map((ex, i) => (
                <Chip
                  key={i}
                  label={JSON.stringify(ex)}
                  size="small"
                  variant="outlined"
                  onDelete={() => removeExample(i)}
                  sx={{ fontFamily: 'monospace', fontSize: 11 }}
                />
              ))}
            </Box>
            <Box display="flex" gap={1}>
              <TextField
                size="small"
                value={exampleInput}
                onChange={(e) => setExampleInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addExample()}
                placeholder="Wert (JSON oder Text)…"
              />
              <IconButton size="small" onClick={addExample} color="primary">
                <AddIcon />
              </IconButton>
            </Box>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}
